/**
 * Utility functions for transaction operations
 */

/**
 * Extract a meaningful pattern from a transaction description
 * that can be used for creating categorization rules.
 *
 * @param description - The transaction description
 * @returns A normalized pattern suitable for rule matching
 */
export function extractPattern(description: string): string {
  let pattern = description.toLowerCase().trim();

  // For Zelle payments, include the person's name to make it specific
  if (pattern.includes('zelle payment from')) {
    // Keep "zelle payment from [name]"
    return pattern;
  }

  if (pattern.includes('zelle payment to')) {
    // Keep "zelle payment to [name]"
    return pattern;
  }

  // Remove standalone numbers but keep parts of words that contain numbers
  // e.g., keep "7-eleven" but remove standalone transaction IDs
  pattern = pattern.replace(/\b\d+\b/g, '').trim();

  // Remove common transaction noise but keep the meaningful parts
  pattern = pattern.replace(/\s+/g, ' ').trim();

  // If pattern is still long and meaningful, use it
  // Otherwise take first few meaningful words
  const words = pattern.split(/\s+/).filter(w => w.length > 2);

  if (words.length <= 4) {
    return pattern;
  }

  // For longer descriptions, take first 4 meaningful words
  return words.slice(0, 4).join(' ').trim() || description.toLowerCase().substring(0, 30);
}

/**
 * Count how many transactions have the same description
 *
 * @param transactions - Array of all transactions
 * @param description - The description to match
 * @returns The count of matching transactions
 */
export function countSimilarTransactions(
  transactions: Array<{ description: string }>,
  description: string
): number {
  return transactions.filter(t => t.description === description).length;
}

/**
 * Filter transactions by exclusion status and split parent status
 *
 * @param transactions - Array of transactions
 * @param showExcluded - Whether to show excluded transactions
 * @returns Filtered array of transactions (excludes split parents - they're replaced by their children)
 */
export function filterTransactionsByExclusion<T extends { excluded?: boolean; isSplit?: boolean }>(
  transactions: T[],
  showExcluded: boolean
): T[] {
  return transactions.filter(t => {
    // Always hide split parent transactions (they're replaced by their split children)
    if (t.isSplit) return false;

    // Filter by exclusion status
    return showExcluded || !t.excluded;
  });
}
